import axios from "axios";
import {
  BUDGET_CHANGE_TYPE,
  RULE_TYPE,
  defaultChangeTypeForBudgetFamily,
  isRestOfAllocatedBudgetType,
} from "./constants";
import {
  formatIsoDateForInput,
  getAbsoluteBudgetValueError,
  formatCampaignBudgetAmount,
  getAllocateSlotCampaignErrors,
  getAllocatedPercentTotalError,
  getCampaignBudgetCurrencyCode,
  getCampaignScheduledSummary,
  SCHEDULED_SUMMARY_STATUS,
  getDailyBudgetError,
  getMaxBudgetError,
  getMinBudgetError,
  getMinCampaignBudget,
  isAllocateBudgetFamily,
  isBudgetFloorValid,
  isCampaignRestLockedInSlot,
  isDailyBudgetValid,
  isMaxBudgetValid,
  isMinBudgetValid,
  sumPercentOfAllocatedBudget,
} from "./helpers";

/** Validate rule campaigns/schedules for conflicts before activating (list page). */
export async function fetchCampaignScheduleConflictValidation({
  clientId,
  userPlatform = "amazon",
  campaignIds = [],
  scheduleStartDate,
  scheduleEndDate,
  excludeRuleSetId,
}) {
  const start = formatIsoDateForInput(scheduleStartDate);
  const end = formatIsoDateForInput(scheduleEndDate);
  const ids = [...new Set((campaignIds ?? []).map(String).filter(Boolean))];

  if (!clientId) {
    return {
      valid: false,
      issues: [{ issue_type: "CAMPAIGN_SCHEDULE_CONFLICT", message: "Client is not selected." }],
    };
  }
  if (!ids.length || !start || !end) {
    return {
      valid: false,
      issues: [
        {
          issue_type: "CAMPAIGN_SCHEDULE_CONFLICT",
          message:
            "This rule is missing campaigns or schedule dates required to validate activation.",
        },
      ],
    };
  }

  try {
    const { data } = await axios.post(
      `${process.env.REACT_APP_ADS_API}/client/${clientId}/automation-rule-set:validate-rule?source=${userPlatform}&type=${RULE_TYPE}`,
      {
        issue_types: ["CAMPAIGN_SCHEDULE_CONFLICT"],
        campaign_ids: ids,
        schedule_config: {
          start_date: start,
          end_date: end,
        },
        ...(excludeRuleSetId ? { exclude_rule_set_id: excludeRuleSetId } : {}),
      }
    );

    const result = data?.data ?? data ?? {};
    const issues = Array.isArray(result.issues) ? result.issues : [];
    const scheduleIssues = issues.filter((i) => i?.issue_type === "CAMPAIGN_SCHEDULE_CONFLICT");

    return {
      valid: scheduleIssues.length === 0,
      issues: scheduleIssues,
    };
  } catch (error) {
    const message =
      error?.response?.data?.message ??
      error?.response?.data?.error ??
      error?.message ??
      "Failed to validate rule schedule.";
    return {
      valid: false,
      issues: [{ issue_type: "CAMPAIGN_SCHEDULE_CONFLICT", message }],
    };
  }
}

function parseTimeToMinutes(timeStr) {
  if (timeStr == null) return null;
  const s = String(timeStr).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (Number.isNaN(h) || Number.isNaN(min) || min < 0 || min > 59) {
    return null;
  }
  if (h === 24 && min === 0) return 24 * 60;
  if (h < 0 || h > 23) return null;
  return h * 60 + min;
}

function getDaypartingSlotErrors(slots) {
  const errors = {};
  const slotEntries = (slots ?? []).map((slot, index) => ({ slot, index }));

  if (slotEntries.length === 0) return errors;

  const parsed = slotEntries.map(({ slot, index }) => ({
    localId: slot.localId,
    startMin: parseTimeToMinutes(slot.startTime),
    endMin: parseTimeToMinutes(slot.endTime),
    index,
  }));

  for (const { startMin, endMin, index, localId } of parsed) {
    if (startMin == null || endMin == null || startMin === "" || endMin === "") {
      errors[localId] = `Select start or end time (duration ${index + 1}).`;
    } else if (endMin <= startMin) {
      errors[localId] = `End time must be after start time (duration ${index + 1}).`;
    }
  }

  for (let i = 0; i < parsed.length; i++) {
    for (let j = i + 1; j < parsed.length; j++) {
      const a = parsed[i];
      const b = parsed[j];
      if (a.startMin == null || a.endMin == null || b.startMin == null || b.endMin == null) {
        continue;
      }
      if (a.startMin < b.endMin && b.startMin < a.endMin) {
        const msg = `Time slots cannot overlap between duration ${a.index + 1} and ${b.index + 1}.`;
        errors[a.localId] = msg;
        errors[b.localId] = msg;
      }
    }
  }

  return errors;
}

function getSlotDateRangeErrors(form) {
  const errors = {};
  const { scheduleStartDate, scheduleEndDate, slots } = form;
  const slotEntries = (slots ?? []).map((slot, index) => ({ slot, index }));

  slotEntries.forEach(({ slot, index }) => {
    const slotNum = index + 1;
    const { localId } = slot;

    if (!slot.startDate || !slot.endDate) {
      errors[localId] = `Duration ${slotNum}: start and end dates are required.`;
      return;
    }

    if (slot.startDate > slot.endDate) {
      errors[localId] = `Duration ${slotNum}: end date must be on or after start date.`;
      return;
    }

    if (scheduleStartDate && slot.startDate < scheduleStartDate) {
      errors[localId] = `Duration ${slotNum}: start date must be on or after rule start date (${scheduleStartDate}).`;
      return;
    }

    if (scheduleEndDate && slot.endDate > scheduleEndDate) {
      errors[localId] = `Duration ${slotNum}: end date must be on or before rule end date (${scheduleEndDate}).`;
    }
  });

  for (let i = 0; i < slotEntries.length; i++) {
    for (let j = i + 1; j < slotEntries.length; j++) {
      const a = slotEntries[i].slot;
      const b = slotEntries[j].slot;
      if (!a.startDate || !a.endDate || !b.startDate || !b.endDate) continue;
      if (a.startDate <= b.endDate && b.startDate <= a.endDate) {
        const slotNumA = slotEntries[i].index + 1;
        const slotNumB = slotEntries[j].index + 1;
        const msg = `Slot durations cannot overlap between duration ${slotNumA} and ${slotNumB}.`;
        if (!errors[a.localId]) errors[a.localId] = msg;
        if (!errors[b.localId]) errors[b.localId] = msg;
      }
    }
  }

  return errors;
}

/** Per-slot duration validation messages keyed by slot localId. */
export function getSlotDurationErrors(form) {
  if (!form) return {};
  if (form.automationMode === "DAYPARTING") {
    return getDaypartingSlotErrors(form.slots);
  }
  return getSlotDateRangeErrors(form);
}

export function validateDaypartingSlots(slots) {
  const errors = getDaypartingSlotErrors(slots);
  return Object.values(errors)[0] ?? "";
}

export function validateForm(form) {
  if (!form.ruleName?.trim()) {
    return "Rule name is required.";
  }
  if (!form.selectedCampaignIds?.length) {
    return "Select at least one campaign.";
  }
  if (!form.scheduleStartDate || !form.scheduleEndDate) {
    return "Select schedule start and end dates.";
  }
  if (form.scheduleStartDate > form.scheduleEndDate) {
    return "Rule end date must be on or after rule start date.";
  }
  if (!form.selectedDays?.length) {
    return "Select at least one day for the schedule.";
  }

  const slotErrors = getSlotDurationErrors(form);
  const firstSlotError = Object.values(slotErrors)[0];
  if (firstSlotError) return firstSlotError;

  const minLabel = `${getMinCampaignBudget()} ${getCampaignBudgetCurrencyCode()}`;

  for (const mid of form.selectedCampaignIds ?? []) {
    const name = form.campaignMeta?.[mid]?.name ?? "Campaign";
    const dailyVal = form.campaignTableBudgets?.[mid]?.base_value;
    if (!isDailyBudgetValid(dailyVal)) {
      const err =
        getDailyBudgetError(dailyVal) ||
        (dailyVal === "" || dailyVal == null
          ? `Daily budget is required (minimum ${minLabel})`
          : `Daily budget must be at least ${minLabel}`);
      return `${name}: ${err}`;
    }

    const minVal = form.campaignTableBudgets?.[mid]?.min_value;
    if (!isMinBudgetValid(minVal)) {
      const err =
        getMinBudgetError(minVal) ||
        (minVal === "" || minVal == null
          ? `Min budget is required (minimum ${minLabel})`
          : `Min budget must be at least ${minLabel}`);
      return `${name}: ${err}`;
    }

    const maxVal = form.campaignTableBudgets?.[mid]?.max_value;
    if (!isMaxBudgetValid(maxVal)) {
      const err =
        getMaxBudgetError(maxVal) ||
        (maxVal === "" || maxVal == null
          ? `Max budget is required (minimum ${minLabel})`
          : `Max budget must be at least ${minLabel}`);
      return `${name}: ${err}`;
    }
  }

  const allocateFamily = isAllocateBudgetFamily(form);

  for (const [slotIndex, slot] of (form.slots ?? []).entries()) {
    for (const mid of form.selectedCampaignIds ?? []) {
      const name = form.campaignMeta?.[mid]?.name ?? "Campaign";
      const slotRow = slot.campaignBudgets?.[mid] ?? {};
      const changeType =
        slotRow.change_type ?? defaultChangeTypeForBudgetFamily(form.budgetFamily);
      const changeVal = slotRow.change_value;
      if (allocateFamily && isCampaignRestLockedInSlot(form, slot, mid)) continue;

      if (changeType === BUDGET_CHANGE_TYPE.ABSOLUTE) {
        if (!isBudgetFloorValid(changeVal)) {
          const err =
            getAbsoluteBudgetValueError(changeVal, changeType) ||
            (changeVal === "" || changeVal == null
              ? `Value is required (minimum ${minLabel})`
              : `Value must be at least ${minLabel}`);
          return `${name} (duration ${slotIndex + 1}): ${err}`;
        }
      }

      if (changeType === BUDGET_CHANGE_TYPE.PERCENT_OF_ALLOCATED_BUDGET) {
        if (changeVal === "" || changeVal == null) {
          return `${name} (duration ${slotIndex + 1}): Value is required.`;
        }
        if (allocateFamily) {
          const percentTotalError = getAllocatedPercentTotalError(
            sumPercentOfAllocatedBudget(form, mid)
          );
          if (percentTotalError) {
            return `${name}: ${percentTotalError}`;
          }
        }
      }

      if (isRestOfAllocatedBudgetType(changeType)) {
        const restCount = (form.slots ?? []).filter((s) =>
          isRestOfAllocatedBudgetType(s?.campaignBudgets?.[mid]?.change_type)
        ).length;
        if (restCount > 1) {
          return `${name}: Rest of Daily Budget can only be used once per campaign.`;
        }
      }

      if (allocateFamily) {
        const { effectiveError } = getAllocateSlotCampaignErrors(form, slot, mid);
        if (effectiveError) {
          return `${name} (duration ${slotIndex + 1}): ${effectiveError}`;
        }
      }
    }
  }

  if (allocateFamily) {
    for (const mid of form.selectedCampaignIds ?? []) {
      const summary = getCampaignScheduledSummary(form, mid);
      if (summary.status === SCHEDULED_SUMMARY_STATUS.OVER) {
        return `${summary.name}: Scheduled budget is over daily budget by ${formatCampaignBudgetAmount(
          summary.overBy
        )}.`;
      }
    }
  }

  return "";
}